import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import HireBanner from '../components/HireBanner';

const services = [
  {
    tag: 'Autonomous Intelligence',
    title: 'AI Agentic Solutions',
    desc: 'We build AI agents that actually get things done. A support agent that resolves tickets without a human in the loop. An ops agent that pulls data, makes a decision, and fires off the next action automatically. We design these systems from scratch, built around your specific workflows, not a generic template.',
    features: [
      'Custom LLM-powered agent design & architecture',
      'Multi-agent orchestration and coordination systems',
      'Tool use, external API & database integration',
      'Autonomous decision-making and task execution',
      'Continuous learning, feedback loops & optimization',
      'RAG (Retrieval-Augmented Generation) systems',
    ],
    icon: (
      <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 2a4 4 0 0 1 4 4v1h1a3 3 0 0 1 3 3v7a3 3 0 0 1-3 3H7a3 3 0 0 1-3-3v-7a3 3 0 0 1 3-3h1V6a4 4 0 0 1 4-4z"/>
        <circle cx="9" cy="13" r="1"/><circle cx="15" cy="13" r="1"/>
        <path d="M9 17s1 1 3 1 3-1 3-1"/>
      </svg>
    ),
  },
  {
    tag: 'Full-Stack Engineering',
    title: 'Software Development',
    desc: 'We handle the whole thing, from the first system design call to production deployment. Clean code, thoughtful architecture, APIs that your team can actually maintain. We build for what your business will be in two years, not just what it is today.',
    features: [
      'React, Next.js & modern frontend development',
      'Node.js, Python, FastAPI & backend engineering',
      'REST, GraphQL & WebSocket API design',
      'Database architecture, design & optimization',
      'Cloud-native architecture & microservices',
      'DevOps, CI/CD pipelines & infrastructure setup',
    ],
    icon: (
      <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/>
        <line x1="12" y1="2" x2="12" y2="22"/>
      </svg>
    ),
  },
  {
    tag: 'Process Intelligence',
    title: 'End-to-End Workflow Automation',
    desc: "We sit with you, map out what's actually happening in your operations, then build the automations that make it run without people. Lead capture to CRM, approvals to invoices, reports that generate themselves. The manual work disappears and the results stay.",
    features: [
      'Full business process mapping & optimization',
      'AI-powered RPA and intelligent automation bots',
      'CRM, ERP & SaaS tool integrations',
      'n8n, Zapier & custom middleware solutions',
      'Real-time monitoring dashboards & alerting',
      'Human-in-the-loop escalation workflows',
    ],
    icon: (
      <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="7" width="6" height="6" rx="1"/>
        <rect x="16" y="7" width="6" height="6" rx="1"/>
        <rect x="9" y="14" width="6" height="6" rx="1"/>
        <path d="M8 10h8M12 10v4"/>
      </svg>
    ),
  },
  {
    tag: 'Data Intelligence',
    title: 'Data Analytics',
    desc: "Most businesses are sitting on more data than they realize and most of it is scattered across exports, logs, and spreadsheets nobody looks at. We build the pipelines that collect it, clean it, and surface it in dashboards your team will actually use. And when there's a genuine use case for ML, we build and deploy those models too.",
    features: [
      'Data pipeline engineering & ETL automation',
      'Custom BI dashboards & real-time reporting',
      'Machine learning model development & deployment',
      'Predictive analytics & business forecasting',
      'Data cleaning, validation & quality assurance',
      'Integration with existing BI tools (Power BI, Grafana)',
    ],
    icon: (
      <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
        <line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/>
        <line x1="6" y1="20" x2="6" y2="14"/><path d="M2 20h20"/>
      </svg>
    ),
  },
];

const ServicesPage: React.FC = () => {
  const gridRef = useRef<HTMLDivElement>(null);
  const ctaRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.reveal').forEach((el, i) => {
              setTimeout(() => el.classList.add('visible'), i * 120);
            });
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.05 }
    );
    if (gridRef.current) observer.observe(gridRef.current);
    if (ctaRef.current) observer.observe(ctaRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div className="svcpage">
      {/* Page hero */}
      <div className="svcpage-hero">
        <div className="orb orb-1" style={{ opacity: 0.5 }} />
        <div className="orb orb-2" style={{ opacity: 0.4 }} />
        <div className="hero-grid" />
        <div className="container">
          <span className="section-label" style={{ justifyContent: 'center', display: 'flex', marginBottom: 16 }}>
            Our Services
          </span>
          <h1>
            What We<br />
            <span className="gt">Actually Build</span>
          </h1>
          <p>
            Four things, done properly. AI agents, custom software, workflow
            automation, and data systems. Each one built for how your
            business actually operates.
          </p>
        </div>
      </div>

      {/* Service cards */}
      <div className="container" ref={gridRef}>
        <div className="svcpage-grid">
          {services.map((s, i) => (
            <div className={`svcpage-card reveal d${(i % 2) + 1}`} key={s.title}>
              <div className="svcpage-card-glow" />
              <div className="svcpage-icon">{s.icon}</div>
              <span className="svcpage-card-tag">{s.tag}</span>
              <h2>{s.title}</h2>
              <p>{s.desc}</p>
              <ul className="svcpage-features">
                {s.features.map(f => <li key={f}>{f}</li>)}
              </ul>
            </div>
          ))}
        </div>
      </div>

      <HireBanner />

      <div className="container" ref={ctaRef}>
        {/* Bottom CTA */}
        <div className="svcpage-cta-row reveal">
          <div>
            <p className="svcpage-cta-text">
              Ready to get started?
            </p>
            <p style={{ color: 'var(--text-muted)', marginTop: 6 }}>
              Let's talk about your project and find the right solution.
            </p>
          </div>
          <Link to="/contact" className="btn btn-primary">
            Get In Touch
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ServicesPage;
